/**
 * Cookie consent levels — единая модель и localStorage-протокол.
 *
 * Канон: 152-ФЗ ст. 9 ч. 4 — «конкретность» согласия. Уровни:
 *  - necessary  — implicit, правовое основание: исполнение договора + безопасность.
 *  - analytics  — opt-in, consent_type cookie_analytics (Метрика, GA).
 *  - marketing  — opt-in, consent_type cookie_marketing (ретаргетинг, пиксели).
 *
 * Источник истины при работе пользователя без авторизации — localStorage.
 * Серверное состояние (`uam.user_consents`) — для авторизованных, синхронизируется
 * через `/consents/grant` и `/consents/revoke` после того, как пользователь сделал выбор.
 *
 * Используется:
 *  - `components/Legal/CookieBanner.jsx` — публичный баннер
 *  - `services/seo.service.js` — gate загрузки Метрики/GA до согласия analytics
 *  - аналогичная утилита есть в UAM (`markbaseCORE/modules/uam/frontend/src/utils/cookieConsentLevels.js`),
 *    которая пишет в тот же localStorage key — состояние согласовано между доменами через cookies + LS.
 */

export const COOKIE_LS_KEY = 'mb_cookie_consent_levels';
export const COOKIE_LEGACY_KEY = 'mb_cookie_consent';
export const COOKIE_LEVEL_CHANGE_EVENT = 'mb:cookie-consent-changed';

const DEFAULT_LEVELS = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
});

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function readLevels() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_LS_KEY);
    if (!raw) return null;
    const parsed = safeParse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const levels = parsed.levels;
    if (!levels || typeof levels !== 'object') return null;
    return {
      necessary: true,
      analytics: levels.analytics === true,
      marketing: levels.marketing === true,
    };
  } catch {
    return null;
  }
}

export function hasUserChoice() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
  try {
    return Boolean(
      localStorage.getItem(COOKIE_LS_KEY) || localStorage.getItem(COOKIE_LEGACY_KEY),
    );
  } catch {
    return false;
  }
}

export function writeLevels(partialLevels, source = 'banner') {
  const merged = { ...DEFAULT_LEVELS, ...(partialLevels || {}), necessary: true };
  const payload = {
    levels: merged,
    updated_at: Date.now(),
    source,
  };
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return payload;
  try {
    localStorage.setItem(COOKIE_LS_KEY, JSON.stringify(payload));
    localStorage.setItem(
      COOKIE_LEGACY_KEY,
      JSON.stringify({ migrated_to: COOKIE_LS_KEY, ts: payload.updated_at }),
    );
  } catch {
    // приватный режим / переполнение — серверное состояние остаётся источником истины
  }
  try {
    if (typeof window.CustomEvent === 'function') {
      window.dispatchEvent(
        new window.CustomEvent(COOKIE_LEVEL_CHANGE_EVENT, { detail: { levels: merged, source } }),
      );
    }
  } catch {
    // ignore: CustomEvent может быть недоступен в очень старых браузерах
  }
  return payload;
}

export function isAnalyticsAllowed() {
  const levels = readLevels();
  return Boolean(levels && levels.analytics);
}

export function isMarketingAllowed() {
  const levels = readLevels();
  return Boolean(levels && levels.marketing);
}

/**
 * Подписаться на изменения уровней — реагирует на CustomEvent (в этой вкладке)
 * и `storage` event (изменения в других вкладках или из UAM).
 * Возвращает функцию отписки.
 */
export function onLevelsChanged(handler) {
  if (typeof window === 'undefined') return () => {};
  const eventHandler = (e) => {
    const levels = e?.detail?.levels || readLevels();
    if (levels) handler(levels, e);
  };
  const storageHandler = (e) => {
    if (e.key === COOKIE_LS_KEY) {
      const levels = readLevels();
      if (levels) handler(levels, e);
    }
  };
  window.addEventListener(COOKIE_LEVEL_CHANGE_EVENT, eventHandler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(COOKIE_LEVEL_CHANGE_EVENT, eventHandler);
    window.removeEventListener('storage', storageHandler);
  };
}
